import {
  Controller,
  Control,
  UseFormGetValues,
  FieldErrors,
} from 'react-hook-form';
import { Concentration, ConcentrationFormData } from 'interfaces/concentration';
import { FC, useMemo } from 'react';
import { calculateVariablePrecision, validatePrecision } from 'helpers/drugs';
import { useTranslation } from 'react-i18next';
import DecimalInput from 'components/UI/DecimalInput/DecimalInput';
import ReactTooltip from 'react-tooltip';
import {
  ConcentrationTypesEnum,
  DOSE_MODE_TYPES_VALUES,
  DrugUnitsEnum,
} from 'constants/drugs';

/**
 * @description Props for the DoseAmountLimits component
 * @typedef {Object} DoseAmountLimitsProps
 * @property {Concentration} record - The concentration record being edited
 * @property {Control<ConcentrationFormData, any>} control - React Hook Form control object
 * @property {UseFormGetValues<ConcentrationFormData>} getValues - Function to get form values
 * @property {FieldErrors<ConcentrationFormData>} errors - Form validation errors
 * @property {string | number | null | undefined} watchDoseLHL - Watched value for dose lower hard limit
 * @property {string | number | null | undefined} watchDoseLSL - Watched value for dose lower soft limit
 * @property {string | number | null | undefined} watchDoseUSL - Watched value for dose upper soft limit
 * @property {string | number | null | undefined} watchDoseUHL - Watched value for dose upper hard limit
 * @property {{label: string, value: string} | null | undefined} watchDoseModeType - Watched value for dose mode type
 * @property {boolean} canManage - Whether the user has permission to manage the form
 */
type DoseAmountLimitsProps = {
  record: Concentration;
  control: Control<ConcentrationFormData, any>;
  getValues: UseFormGetValues<ConcentrationFormData>;
  errors: FieldErrors<ConcentrationFormData>;
  watchDoseLHL: string | number | null | undefined;
  watchDoseLSL: string | number | null | undefined;
  watchDoseUSL: string | number | null | undefined;
  watchDoseUHL: string | number | null | undefined;
  watchDoseModeType:
    | {
        label: string;
        value: string;
      }
    | null
    | undefined;
  canManage: boolean;
};

/**
 * @description Component for managing dose amount limits with validation.
 * Validates order (LHL < LSL < USL < UHL), min/max values, and precision.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DoseAmountLimitsProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const DoseAmountLimits: FC<DoseAmountLimitsProps> = ({
  record,
  control,
  getValues,
  errors,
  watchDoseLHL,
  watchDoseLSL,
  watchDoseUSL,
  watchDoseUHL,
  watchDoseModeType,
  canManage,
}) => {
  const { t } = useTranslation();

  const precisionDoseLHL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseLHL),
    [watchDoseLHL],
  );
  const precisionDoseLSL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseLSL),
    [watchDoseLSL],
  );
  const precisionDoseUSL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseUSL),
    [watchDoseUSL],
  );
  const precisionDoseUHL = useMemo<any>(
    () => calculateVariablePrecision(watchDoseUHL),
    [watchDoseUHL],
  );

  return (
    <div className="row pb-3 flex-nowrap">
      <div className="device-concentration-form__label">{t('device.dose')}</div>

      {/* Lower hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="dose_lower_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LSL, USL, UHL] = getValues([
                  'dose_lower_soft_limit',
                  'dose_upper_soft_limit',
                  'dose_upper_hard_limit',
                ]);

                isValid = isValid && (LSL ? Number(LSL) > currentValue : true);
                isValid = isValid && (USL ? Number(USL) > currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneLowerLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDoseLSL !== '' &&
                    watchDoseLSL !== null &&
                    !isNaN(watchDoseLSL as any))
                );
              },
              precision: (value) => validatePrecision(value, precisionDoseLHL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDoseLHL}
              rounding={'toAllowedNumberOfDecimals'}
              // disabled={!canManage}
              className={
                'device-concentration__input--hard-limit ' +
                (errors?.dose_lower_hard_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="dose_lower_hard_limit"
              data-testid="dose_lower_hard_limit"
              disabled={!canManage}
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.dose_lower_hard_limit && (
          <ReactTooltip
            id="dose_lower_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.dose_lower_hard_limit?.type === 'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.dose_lower_hard_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.dose_lower_hard_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.dose_lower_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.dose_lower_hard_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDoseLHL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Lower soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="dose_lower_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, USL, UHL] = getValues([
                  'dose_lower_hard_limit',
                  'dose_upper_soft_limit',
                  'dose_upper_hard_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (USL ? Number(USL) > currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneLowerLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDoseLHL !== '' &&
                    watchDoseLHL !== null &&
                    !isNaN(watchDoseLHL as any))
                );
              },
              precision: (value) => validatePrecision(value, precisionDoseLSL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDoseLSL}
              rounding={'toAllowedNumberOfDecimals'}
              // disabled={!canManage}
              className={
                'device-concentration__input--soft-limit ' +
                (errors?.dose_lower_soft_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="dose_lower_soft_limit"
              data-testid="dose_lower_soft_limit"
              disabled={!canManage}
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.dose_lower_soft_limit && (
          <ReactTooltip
            id="dose_lower_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.dose_lower_soft_limit?.type === 'oneLowerLimitRequired' &&
              t('device.errors.one_lower_limit__required')}
            {errors?.dose_lower_soft_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.dose_lower_soft_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.dose_lower_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.dose_lower_soft_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDoseLSL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Upper soft limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="dose_upper_soft_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, LSL, UHL] = getValues([
                  'dose_lower_hard_limit',
                  'dose_lower_soft_limit',
                  'dose_upper_hard_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (LSL ? Number(LSL) < currentValue : true);
                isValid = isValid && (UHL ? Number(UHL) > currentValue : true);

                return isValid;
              },
              oneUpperLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDoseUHL !== '' &&
                    watchDoseUHL !== null &&
                    !isNaN(watchDoseUHL as any))
                );
              },
              precision: (value) => validatePrecision(value, precisionDoseUSL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDoseUSL}
              rounding={'toAllowedNumberOfDecimals'}
              // disabled={!canManage}
              className={
                'device-concentration__input--soft-limit ' +
                (errors?.dose_upper_soft_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="dose_upper_soft_limit"
              data-testid="dose_upper_soft_limit"
              disabled={!canManage}
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.dose_upper_soft_limit && (
          <ReactTooltip
            id="dose_upper_soft_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.dose_upper_soft_limit?.type === 'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.dose_upper_soft_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.dose_upper_soft_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.dose_upper_soft_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.dose_upper_soft_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDoseUSL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Upper hard limit */}
      <div className="device-concentration__box-input">
        <Controller
          control={control}
          name="dose_upper_hard_limit"
          // v8 ignore start -- WAIVER(THIRDPARTY): react-hook-form validate rules executed inside the RHF Controller config; the uncovered cross-limit order ternaries (LHL/LSL/USL/UHL comparisons) and input-shape guards need full-form value matrices, exercised representatively by ConcentrationForm form-level tests and the E2E suite
          rules={{
            min: 0.001,
            max: 9999,
            validate: {
              increasingOrder: (value) => {
                if (!value || isNaN(Number(value))) return true;

                const currentValue = Number(value);
                let isValid = true;

                const [LHL, LSL, USL] = getValues([
                  'dose_lower_hard_limit',
                  'dose_lower_soft_limit',
                  'dose_upper_soft_limit',
                ]);

                isValid = isValid && (LHL ? Number(LHL) < currentValue : true);
                isValid = isValid && (LSL ? Number(LSL) < currentValue : true);
                isValid = isValid && (USL ? Number(USL) < currentValue : true);

                return isValid;
              },
              oneUpperLimitRequired: (value) => {
                return (
                  (value !== '' && value !== null && !isNaN(value as any)) ||
                  (watchDoseUSL !== '' &&
                    watchDoseUSL !== null &&
                    !isNaN(watchDoseUSL as any))
                );
              },
              precision: (value) => validatePrecision(value, precisionDoseUHL),
            },
          }}
          // v8 ignore stop -- WAIVER(THIRDPARTY)
          render={({ field: { onChange, onBlur, value, ref } }) => (
            <DecimalInput
              value={String(value)}
              onChange={onChange}
              onBlur={onBlur}
              precision={precisionDoseUHL}
              rounding={'toAllowedNumberOfDecimals'}
              // disabled={!canManage}
              className={
                'device-concentration__input--hard-limit ' +
                (errors?.dose_upper_hard_limit ? 'has-error' : '')
              }
              ref={ref}
              data-tip
              data-for="dose_upper_hard_limit"
              data-testid="dose_upper_hard_limit"
              disabled={!canManage}
            />
          )}
        />

        {/* v8 ignore start -- WAIVER(MICRO): error-tooltip rendering artifact; per-error-type message branches are display-only and depend on validation states not all reachable in an isolated unit render; error display is asserted by ConcentrationForm form-level tests */}
        {errors?.dose_upper_hard_limit && (
          <ReactTooltip
            id="dose_upper_hard_limit"
            effect="solid"
            place="bottom"
            delayHide={300}
            type="error"
          >
            {errors?.dose_upper_hard_limit?.type === 'oneUpperLimitRequired' &&
              t('device.errors.one_upper_limit__required')}
            {errors?.dose_upper_hard_limit?.type === 'min' &&
              t('device.errors.not_lower_than', {
                value: '0.001',
              })}
            {errors?.dose_upper_hard_limit?.type === 'max' &&
              t('device.errors.not_greater_than', {
                value: '9999',
              })}
            {errors?.dose_upper_hard_limit?.type === 'increasingOrder' &&
              t('device.errors.order_LHL_LSL_USL_UHL')}
            {errors?.dose_upper_hard_limit?.type === 'precision' &&
              t('device.errors.precision', {
                value: precisionDoseUHL,
              })}
          </ReactTooltip>
        )}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
      </div>

      {/* Units */}
      <div className="device-concentration-units">
        {/* v8 ignore start -- WAIVER(MICRO): display-only units-label ternary choosing between the persisted record value and the draft form selection; pure formatting artifact with no assertable logic */}
        {record?.type === ConcentrationTypesEnum.MILLILITER_BASED
          ? DrugUnitsEnum.MILLILITER
          : record?.drug_unit}
        {/* v8 ignore stop -- WAIVER(MICRO) */}
        &nbsp;
        {!!record &&
          !!watchDoseModeType &&
          DOSE_MODE_TYPES_VALUES[
            watchDoseModeType.value as keyof typeof DOSE_MODE_TYPES_VALUES
          ]}
      </div>
    </div>
  );
};

export default DoseAmountLimits;
